import cn from 'classnames'
import Image from 'next/image'
import {FC} from 'react'

import {getBlogContent} from '@/content/blog/blog'
import {Link, useTranslation} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Button} from '../content/Button'
import {ButtonSize} from '../content/ButtonTypes'
import {Text, TextVariant} from '../content/Text'
import {ArrowIcon} from '../icons/ArrowIcon'

type AcademyBannerProps = {
  isCentered?: boolean
}

export const AcademyBanner: FC<React.PropsWithChildren<AcademyBannerProps>> = ({
  isCentered = false,
}) => {
  const {t} = useTranslation('page')
  const {title, buttonLinkHref, buttonLinkText} = getBlogContent().academyBanner

  return (
    <>
      <Link href={buttonLinkHref} legacyBehavior>
        <a className={cn('banner', isCentered && 'centered')}>
          <div className="image">
            <Image
              src="/images/hero/academy.svg"
              alt="Trademark academy"
              style={{objectFit: 'contain'}}
              fill
              sizes={`${MOBILE_SCREENSIZE} 84px, 185px`}
            />
          </div>
          <div>
            <Text variant={TextVariant.h5} element="p">
              {t(title)}
            </Text>
            <div className="button-wrapper">
              <Button
                icon={<ArrowIcon size={IconSize.small} />}
                type="button"
                size={ButtonSize.small}
                textVariant={TextVariant.subtitle13}
                isHoverable
              >
                {t(buttonLinkText)}
              </Button>
            </div>
          </div>
        </a>
      </Link>

      <style jsx>{`
        .banner {
          display: block;
          width: 100%;
          border: 1px solid ${theme.palette.gray[200]};
          padding: ${theme.spacing[3.5]};
          border-radius: 16px;
        }

        .banner:hover {
          border-color: ${theme.palette.gray[300]};
        }

        .image {
          position: relative;
          width: 185px;
          height: 118px;
          margin-bottom: ${theme.spacing[3]};
        }

        .button-wrapper {
          margin-top: ${theme.spacing[3]};
        }

        .centered {
          text-align: center;
        }

        .centered .image {
          margin-left: auto;
          margin-right: auto;
          margin-bottom: ${theme.spacing[3]};
        }

        @media ${TABLET_SCREENSIZE} {
          .banner:not(.centered) {
            display: flex;
            align-items: center;
            border: 0;
            padding: 0;
          }

          .image {
            margin-right: ${theme.spacing[3]};
            margin-left: 0;
            margin-bottom: 0;
          }

          .button-wrapper {
            margin-top: ${theme.spacing[2]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 84px;
            height: 48px;
            flex: 0 0 84px;
          }
        }
      `}</style>
    </>
  )
}
